/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { ReactNode, useEffect, useReducer, useState } from "react";
import { FirebaseError, initializeApp } from "firebase/app";
import {
  Auth,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  updateEmail,
  User,
} from "firebase/auth";
import { EJSON, ObjectId } from "bson";
import axios from "axios";

import { handleFirebaseError } from "./errors";
import SignIn from "components/signin";
import { Loading } from "components/Loading";
import { convertISOStringsToDates } from "./functions";

interface FirebaseAppContextInterface {
  auth: Auth;
  functions: (data: [string, any]) => Promise<string | number | KV | KV[]>;
  currentUser?: User | null;
  userObject: KV | null;
  refreshCustomData: () => Promise<void>;
  initialized: boolean;
  register: (
    email: string,
    password: string,
  ) => Promise<string | null>;
  logIn: (email: string, password: string) => Promise<string | null>;
  logOut: () => Promise<void>;
  changeEmail: (newEmail: string) => Promise<void>;
  deleteUser: (userId: string) => Promise<string | null>;
  createId: () => string,
  state: KV,
  setState: (key: string | undefined | null, data: KV) => void,
  dispatch: React.Dispatch<KV>,
  resources: KV[]
}

const app = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
});

export const firebaseErrorMessage = (error: unknown) => {
  if (!(error instanceof FirebaseError)) {
    return error instanceof Error ? error.message : "エラーが発生しました。";
  }

  switch (error.code) {
    case "auth/invalid-email":
      return "メールアドレスが正しくありません。";
    case "auth/user-not-found":
      return "そのメールアドレスを持つユーザーが見つかりませんでした。";
    case "auth/wrong-password":
      return "パスワードが間違っています。";
    case "auth/user-disabled":
      return "このアカウントは無効になっています。";
    case "auth/email-already-in-use":
      return "このメールアドレスはすでに別のアカウントで使用されています。";
    case "auth/weak-password":
      return "パスワードが弱すぎます。";
    case "auth/popup-closed-by-user":
      return "認証が完了する前にポップアップが閉じられました。";
    case "auth/cancelled-popup-request":
      return "ポップアップのリクエストがキャンセルされました。";
    case "auth/unverified-email":
      return "メールアドレスが認証されていません。";
    case "auth/too-many-requests":
      return "リクエストが多すぎます。しばらくしてからもう一度お試しください。";
    case "auth/operation-not-allowed":
      return "この操作は許可されていません。";
    case "auth/requires-recent-login":
      return "この操作には最近のログインが必要です。";
    case "auth/invalid-api-key":
      return "APIキーが無効です。";
    case "auth/app-not-authorized":
      return "この操作は許可されていません。";
    default:
      return "認証エラーが発生しました。";
  }
};

const auth = getAuth(app);

export const FirebaseAppContext = React.createContext<FirebaseAppContextInterface>(
  {} as FirebaseAppContextInterface,
);

export const useFirebaseApp = () => {
  const appContext = React.useContext(FirebaseAppContext);
  if (!appContext) {
    throw new Error(
      `You must call useFirebaseApp() inside of a <FirebaseAppProvider />`
    );
  }

  return appContext;
};

export const isExternal = (app: FirebaseAppContextInterface) => (!app.userObject?.internal)

export const RequireLoggedInUser = ({ children }: {children: JSX.Element}): JSX.Element => {
  const firebaseAppContext = useFirebaseApp();
  const reset = async (email:string, password:string) => {
    await sendPasswordReset(email)
  }
  
  // ログイン状態の確認が終わるまで（currentUser が undefined の間）は、ログイン画面ではなく読み込み中を表示する
  if (firebaseAppContext.currentUser === undefined) return <Loading full />
  return (firebaseAppContext.currentUser ? children :
    <SignIn 
      signin={(email:string, password:string) => firebaseAppContext.logIn(email, password)}
      reset={reset}
    />
  )
};

export const isEmailVefied = (user?: User | null) => {
  if (!user) return false;
  if (
    user.providerData.find((v) => v.providerId === "password") &&
    !user.emailVerified
  )
    return false;

  return true;
};


/**
 * Call Google Cloud Run function.
 * Unauthenticated call is allowed, but checked at portal function that require authentication of same firebase project.
 * @param payloads Attributes to pass to the function
 * @returns Returned value from executed cloud run function. Transformed to proper javascript object.
 */
const functions = async (payloads: [string, any]) => {
  try {
    const cloudRunUrl = process.env.REACT_APP_CLOUDRUN_MONGO_URL ?? "";
    const authToken = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    };

    const axiosInstance = axios.create({
      transformRequest: [(data) => {
        const dateConverted = data;// convertISOStringsToDates(data);
        return EJSON.stringify(dateConverted);
      }],
      transformResponse: [(data) => {
          try {
            const parsed = EJSON.parse(data);
            return parsed;// convertISOStringsToDates(parsed);
          } catch (error) {
            try {
              const jsonParsed = JSON.parse(data);
              return jsonParsed;//convertISOStringsToDates(jsonParsed);
            } catch (e) {
              return data;
            }
          }
        }
      ]
    });

    const response = await axiosInstance.post(cloudRunUrl, payloads, {
      headers
    });

    if (response.status === 500) {
      throw new Error(response.data.message)
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`Cloud Run request failed: ${error.response?.data?.message ?? error.response?.data ?? error.message}`);
    }
    throw error;
  }
};

/**
 * Send verification email using own SMTP.  Use confirmEmail in firebase function
 * @param user User to send verification
 * @returns Error message or null if succeed
 */
export const sendVerification = async (
  email?: string | null,
) => {
  try {
    if (!email) return "メールアドレスが正しく設定されていません。";
    let result;
    result = await functions(["confirmEmail", email]);
    if (result) throw new Error(result as string);

    return null;
  } catch (e) {
    return e instanceof Error
      ? e.message
      : "認証メールが送信できませんでした。";
  }
};

/**
 * Send password reset email using own SMTP.  Use resetPassword in firebase function
 * @param email Email to send password reset
 * @returns Error message or null if succeed
 */
export const sendPasswordReset = async (email?: string | null) => {
  try {
    if (!email) return "メールアドレスが正しく設定されていません。";
    const result = await functions(["resetPassword", email]);
    if (result) throw new Error(result as string);

    return null;
  } catch (e) {
    return e instanceof Error ? e.message : e;
  }
};

export const FirebaseAppProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = React.useState<User | null>();
  const [userObject, setUserObject] = React.useState<KV | null>(null);
  const [initialized, setInitialized] = React.useState<boolean>(false);

  // Used in useUsa002DbActions.  Set error handling at useUsa002DbActions.  Create user in db here.
  const register = async (
    email: string,
    password: string,
  ) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      if (userCredential?.user) {
        await functions([
          "auth/createUser",
          [userCredential.user.uid, userCredential.user.email],
        ]);
        const error = await sendVerification(
          userCredential.user.email,
        );

        return error;
      }

      return "アカウントの作成に失敗しました。";
    } catch (e) {
      return handleFirebaseError(e);
    }
  };

  // sendpasdswordresetemail before auth is in US-G001.  Need to use custom email sending
  // sendemailverification needs to do in server-side if not authenticated
  // updatePassword is called in US-G015

  // Called from US-A001 Login
  const logIn = async (email: string, password: string): Promise<string | null> => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      if (userCredential.user && !isEmailVefied(userCredential.user)) {
        return "メールアドレスが認証されていません。";
      }
      setCurrentUser(userCredential.user);
      return null;
    } catch (e) {
      return handleFirebaseError(e);
    }
  };

  // Called from US-A022 Logout and several other logout handler
  const logOut = async () => {
    await auth.signOut();
    setCurrentUser(null);
    // セッションストレージクリア
    sessionStorage.clear();
  };

  // Called from useUsg017DbActions (Logged in status is required);
  const changeEmail = async (newEmail: string) => {
    if (getAuth().currentUser) {
      await updateEmail(getAuth().currentUser!, newEmail);
    }
  };

  // Usually not called.  Using flag to cancel user.
  const deleteUser = React.useCallback(
    async (userId: string) => {
      if (!currentUser)
        return "認証サーバーから認証情報を取得できません。時間を置いて再度操作するか、一旦ログアウトしてください。";
      if (
        !window.confirm(
          "アカウントを削除します。この操作は取り消せません。よろしいですか？",
        )
      )
        await functions(["auth/deleteUser", [userId, currentUser.email]]); // Set cancelled flag to user db and delete firebase user by admin app
      // await auth.currentUser?.delete(); // Usually not required because above function handles it.  Session invalidate and logout are performed automatically

      return null;
    },
    [currentUser],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      // Get user data from db and set it to userObject
      const func = async () => {
        if (user && !isEmailVefied(user)) {
          await auth.signOut();

          return;
        }
        setCurrentUser(user);
        if (user) {
          if (!userObject || userObject._id !== user.uid) {
            // Mainly initial load, login or user change
            const userData = (await functions([
              "mongo/client",
              { collection: "user", findOne: { filter: { _id: user.uid } } },
            ])) as KV;
            if (userData) {
              if (
                user.email &&
                userData.email !== user.email &&
                !userData.line_id
              ) {
                // User change email and click verification when logged out(When line, User's email is dummy)
                await functions([
                  "mongo/client",
                  {
                    collection: "user",
                    updateOne: {
                      filter: { _id: user.uid },
                      update: { $set: { email: user.email, email_verified: user.emailVerified } },
                    },
                  },
                ]);
                userData.email = user.email;
                userData.email_verified = user.emailVerified;
              } else if (userData.email_verified !== user.emailVerified && !userData.line_id && !userData.google_id) {
                // User click verification when logged out
                await functions([
                  "mongo/client",
                  { collection: "user", updateOne: { filter: { _id: user.uid }, update: { $set: { email_verified: user.emailVerified } } } },
                ]);
                userData.email_verified = user.emailVerified;
              }
              setUserObject(userData);
            } else if (
              !user.providerData.find((v) => v.providerId === "password")
            ) {
              const settingValue = { email: user.email };
              await functions([
                "mongo/client",
                {
                  collection: "user",
                  updateOne: {
                    filter: { _id: user.uid },
                    update: {
                      $set: settingValue,
                      $setOnInsert: {
                        _id: user.uid,
                        status: 0,
                        created_at: new Date(),
                        updated_at: new Date(),
                      },                    },
                    options: { upsert: true },
                  },
                },
              ]);
            }
          } else if (user.email !== userObject.email) {
            // User change email and click verification when logged in
            await functions([
              "mongo/client",
              {
                collection: "user",
                updateOne: {
                  filter: { _id: user.uid },
                  update: { $set: { email: user.email, email_verified: user.emailVerified} },
                },
              },
            ]);
            setUserObject({ ...userObject, email: user.email });
          }
        } else {
          setUserObject(null);
        }
        setInitialized(true);
      };
      func().catch((e: any) => {
        console.error(e);
      });
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get updated user data from db
  const refreshCustomData = async () => {
    if (currentUser) {
      const userData = await functions([
        "mongo/client",
        { collection: "user", findOne: { filter: { _id: currentUser.uid } } },
      ]);
      if (userData) {
        setUserObject(userData as KV);
      } else {
        setUserObject(null);
      }
    }
  };

  const createId = () => new ObjectId().toString()

  const [state, dispatch] = useReducer(
    (state: KV, action: KV) => ({
      ...state,
      ...action,
    }),
    {}
  );
  const setState = (key: string | undefined | null, data: KV): void => {
    if (!key) {
      dispatch(data)
      return
    }
    let newState = { ...state }
    key.split(".").reduce((a: any, r: string, i: number, array: string[]) => {
      if (i === array.length - 1) a[r] = data
      if (!a[r]) a[r] = {}
      return a[r]
    }, newState)
    dispatch(newState)
  }
  const [resources, setResources] = useState<KV[]>([])

  useEffect(() => {
    async function fetchProps() {
        let response = await fetch(process.env.PUBLIC_URL + '/contexts/modality.json', { cache: "reload" })
        const data = await response.json()
        setResources(data)
    }
    fetchProps()
}, [])

  const firebaseAppContext = {
    auth,
    functions,
    currentUser,
    userObject,
    refreshCustomData,
    initialized,
    register,
    logIn,
    logOut,
    changeEmail,
    deleteUser,
    createId,
    state,
    setState,
    dispatch,
    resources,
  };

  return (
    <FirebaseAppContext.Provider value={firebaseAppContext}>
      {children}
    </FirebaseAppContext.Provider>
  );
};
